import { AppError } from "./errors";

export interface User {
    oid: string,
    name: string,
    email?: string
}

export enum ChangeRequestEventType {
    CREATING = 'creating',
    CREATINGERROR = 'creatingerror',
    CREATED = 'created',
    OPEN = 'open',
    APPROVED = 'approved',
    CANCELED = 'canceled',
    APPLYING = 'applying',
    APPLYINGERROR = 'applyingerror',
    COMPLETED = 'completed',
    DISMISSED = 'dismissed',
    ROLLINGBACK = 'rollingback',
    ROLLEDBACK = 'rolledback',
    ERROR = 'error'
}

export enum ChangeRequestAction {
    APPROVE = 'approve',
    UNAPPROVE = 'unapprove',
    APPLY = 'apply',
    ROLLBACK = 'rollback',
    CANCEL = 'cancel',
    UNSEAL = 'unseal',
    DISMISS = 'dismiss'
}

export const ChangeRequestEventTransitions = new Map<ChangeRequestEventType, ChangeRequestAction[]>([
    [ChangeRequestEventType.OPEN, [ChangeRequestAction.APPROVE, ChangeRequestAction.CANCEL]],
    [ChangeRequestEventType.APPROVED, [ChangeRequestAction.APPLY, ChangeRequestAction.UNAPPROVE]],
    [ChangeRequestEventType.COMPLETED, [ChangeRequestAction.ROLLBACK,]],
    [ChangeRequestEventType.CREATINGERROR, [ChangeRequestAction.CANCEL,]],
    [ChangeRequestEventType.APPLYINGERROR, [ChangeRequestAction.DISMISS,]],
    [ChangeRequestEventType.ERROR, [ChangeRequestAction.DISMISS,]]
]);

export const ChangeRequestEventActions = new Map<ChangeRequestEventType, ChangeRequestAction[]>([
    [ChangeRequestEventType.OPEN, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.OPEN)!, ChangeRequestAction.UNSEAL]],
    [ChangeRequestEventType.APPROVED, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.APPROVED)!, ChangeRequestAction.UNSEAL]],
    [ChangeRequestEventType.CREATINGERROR, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.CREATINGERROR)!]],
    [ChangeRequestEventType.APPLYINGERROR, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.APPLYINGERROR)!, ChangeRequestAction.UNSEAL]],
    [ChangeRequestEventType.COMPLETED, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.COMPLETED)!, ChangeRequestAction.UNSEAL]],
    [ChangeRequestEventType.ERROR, [...ChangeRequestEventTransitions.get(ChangeRequestEventType.ERROR)!, ChangeRequestAction.UNSEAL]],
    [ChangeRequestEventType.ROLLEDBACK, [ChangeRequestAction.UNSEAL]]
]);

export interface ChangeRequest {
    id: number,
    ticket: string,
    author: string,
    date: Date,
    state: ChangeRequestEventType
}

export interface SecretRef {
    vault: string,
    name: string
}

export interface SecretWithValue extends SecretRef {
    value: string
}

export interface ChangeRequestEvent {
    type: ChangeRequestEventType,
    date: Date,
    author: User,
}

export interface ChangeRequestDetail {
    id: number,
    ticket: string,
    description: string,
    state: string,
    secrets: SecretRef[],
    history: ChangeRequestEvent[],
    warnings: AppError[],
    actions: Action[],
    transitions: ChangeRequestAction[]
}

export interface ApiUnsealResponse {
    identifier: string,
    value: string,
}

export interface ApiChangeRequestFindResponse {
    id: number
}


export interface Action {
    type: ChangeRequestAction,
    enabled: boolean,
    reason?: AppError[]
}

export type ChangeRequestCreateData = {
    ticket: string,
    description: string,
    secrets: SecretWithValue[]
}
export type ChangeRequestCreateResult = { id: number }


export type ChangeRequestUnsealData = {
    id: number,
    secret: SecretRef
}

// -------------- NEW ------------------------------    

export type ErrCode = "forbidden" | "notfound" | "badrequest" | "unprocessable" | "conflict" | "servererror" | "unknown"
export type Ok<T> = { ok: true, value: T }
export type Err = { ok: false, code: ErrCode, errors: AppError[] }
export type Result<T = any> = Ok<T> | Err

export const Ok = <T = any>(value: T): Ok<T> => ({ ok: true, value })
export const Err = (code: ErrCode, errors: AppError[]): Err => ({ ok: false, code, errors })

export class V1ApiError extends Error {
    code: ErrCode
    errors?: AppError[]

    constructor(code: ErrCode, errors?: AppError | AppError[]) {
        super();
        this.code = code;
        this.errors = errors ? (Array.isArray(errors) ? errors : [errors]) : undefined;
    }

    toJSON() {
        return { code: this.code, errors: this.errors }
    }
}